import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../baseQuery';
import type { TdMomentum, TdPopularity, TdRate } from 'constants/topdecked';

/**
 * Contract for GET /cards/:oracleId/analytics (ticket 1215930107055662 plus
 * Addendum 2). The backend performs ALL sorting, capping, junk filtering, URL
 * absolutization and UTM tagging; the frontend renders what it is given and applies
 * no business logic to ordering or selection. Archetype percentages are omitted from
 * the payload entirely. The cross-format `all` block is dropped server side, so no
 * format labeled "All formats" ever arrives.
 */
export interface CardAnalyticsFormat {
  /** Display-cased label, e.g. "Modern". */
  format: string;
  tier: 'competitive' | 'casual';
  popularity: TdPopularity;
  momentum: TdMomentum;
  /** Format-level percentage (sane and displayable; NOT the archetype percentage). */
  percentage: number;
}

export interface CardAnalyticsDeck {
  name: string;
  /** Absolute topdecked.com URL carrying the backend's TD_UTM_PARAMS. */
  url: string;
  /** Representative card art for the row; null when no confident catalog match. */
  artScryfallId: string | null;
}

export interface CardAnalyticsGameplay {
  format: string;
  keepRate: TdRate;
  keepPriority: TdRate;
}

export interface CardAnalyticsResponse {
  oracleId: string;
  /** Absolute, UTM-tagged (utm_content=card_module). */
  topdeckedUrl: string;
  /** Absolute, UTM-tagged (utm_content=card_module_attribution). */
  attributionUrl: string;
  /** Sorted and capped at TD_FORMATS_DISPLAYED by the backend. */
  formats: CardAnalyticsFormat[];
  /** Capped at TD_DECKS_DISPLAYED by the backend. */
  topDecks: CardAnalyticsDeck[];
  /** Capped at TD_GAMEPLAY_FORMATS_DISPLAYED by the backend. */
  gameplay: CardAnalyticsGameplay[];
}

/**
 * PUBLIC endpoint (works logged-out). `requiresAuth: false` is deliberate, same reason
 * as cardWantersApi: baseQuery defaults the flag to true, which would force a logout on
 * any 401 on a public card page. A 404 (no CardAnalytics row for this oracle id) is the
 * normal "render nothing" case and its body is never read.
 */
export const cardAnalyticsApi = createApi({
  reducerPath: 'cardAnalyticsApi',
  baseQuery: baseQuery,
  tagTypes: ['CardAnalytics'],
  endpoints: (builder) => ({
    getCardAnalytics: builder.query<CardAnalyticsResponse, { oracleId: string }>({
      query: ({ oracleId }) => ({
        url: `/cards/${oracleId}/analytics`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: ['CardAnalytics'],
    }),
  }),
});

export const { useGetCardAnalyticsQuery } = cardAnalyticsApi;
